<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, shallowRef, nextTick } from "vue";
// leaflet.css is dynamically imported inside initMap() so the ~50 KB stylesheet
// is split into its own chunk and only fetched on pages that actually mount the
// map (e.g. /location, apartment detail). The home page bundle stays smaller.
import type {
  Map as LeafletMap,
  Marker,
  CircleMarker,
  Polygon,
  Polyline,
  LatLngTuple,
} from "leaflet";

interface StoredRoute {
  id: string;
  label: string;
  distance_m: number;
  duration_s: number;
  coordinates: [number, number][];
}

interface MapPoint {
  lat: number;
  lng: number;
  label: string;
  mode?: "walk" | "taxi" | "bus" | "drive";
}

interface Territory {
  name?: string;
  coordinates: [number, number][];
}

const props = withDefaults(
  defineProps<{
    center: { lat: number; lng: number; zoom?: number };
    territory?: Territory;
    points?: MapPoint[];
    activeIndex?: number | null;
    height?: string;
    title?: string;
    poster?: string;
  }>(),
  {
    territory: undefined,
    points: () => [],
    activeIndex: null,
    height: "480px",
    title: "",
    poster: "",
  }
);

const emit = defineEmits<{
  (e: "point:hover", index: number | null): void;
}>();

const { t } = useI18n();

const mapEl = ref<HTMLElement>();
const mapInstance = shallowRef<LeafletMap>();
const pointMarkers = shallowRef<CircleMarker[]>([]);
const mainMarker = shallowRef<Marker>();
const territoryPolygon = shallowRef<Polygon>();
const routeLayers = shallowRef<Polyline[]>([]);

const BRAND_GOLD = "#d4af37";
const BRAND_GOLD_DARK = "#b4942e";

function buildMainIcon(L: typeof import("leaflet")) {
  const svg = `
    <div class="dl-pin" aria-hidden="true">
      <svg viewBox="0 0 40 48" width="40" height="48" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="dl-pin-shadow" x="-20%" y="-10%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35" />
          </filter>
        </defs>
        <path d="M20 0C9 0 0 8.6 0 19.2 0 33.6 20 48 20 48s20-14.4 20-28.8C40 8.6 31 0 20 0z"
              fill="${BRAND_GOLD}" stroke="${BRAND_GOLD_DARK}" stroke-width="1.5" filter="url(#dl-pin-shadow)"/>
        <circle cx="20" cy="19" r="7" fill="#ffffff"/>
        <circle cx="20" cy="19" r="3" fill="${BRAND_GOLD_DARK}"/>
      </svg>
    </div>
  `;
  return L.divIcon({
    html: svg,
    className: "dl-map-main-pin",
    iconSize: [40, 48],
    iconAnchor: [20, 48],
    popupAnchor: [0, -44],
  });
}

async function initMap() {
  await nextTick();
  if (!mapEl.value) {
    console.warn("[LocationMap] mapEl ref is not available");
    return;
  }
  let L: typeof import("leaflet");
  try {
    const [mod] = await Promise.all([
      import("leaflet"),
      import("leaflet/dist/leaflet.css"),
    ]);
    L = (mod.default ?? mod) as typeof import("leaflet");
  } catch (err) {
    console.error("[LocationMap] failed to load leaflet", err);
    return;
  }

  const map = L.map(mapEl.value, {
    center: [props.center.lat, props.center.lng],
    zoom: props.center.zoom ?? 13,
    scrollWheelZoom: false,
  });

  // MapTiler when a key is provisioned (preferred — has SLA + domain-restricted
  // keys); single-origin Carto Voyager as fallback for dev/no-key. The legacy
  // {s}.basemaps.cartocdn.com subdomain wildcard was deprecated and dropped
  // tiles intermittently in production (visible 4xx for tiles like 3048@2x.png).
  const maptilerKey = useRuntimeConfig().public.maptilerKey as string;
  const tileUrl = maptilerKey
    ? `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}{r}.png?key=${maptilerKey}`
    : "https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
  const tileAttribution = maptilerKey
    ? '&copy; <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
    : '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener">CARTO</a>';

  L.tileLayer(tileUrl, {
    maxZoom: 19,
    attribution: tileAttribution,
  }).addTo(map);

  if (props.territory?.coordinates?.length) {
    territoryPolygon.value = L.polygon(
      props.territory.coordinates as LatLngTuple[],
      {
        color: BRAND_GOLD_DARK,
        weight: 2,
        opacity: 0.9,
        fillColor: BRAND_GOLD,
        fillOpacity: 0.18,
      }
    ).addTo(map);
    if (props.territory.name) {
      territoryPolygon.value.bindTooltip(props.territory.name, {
        sticky: true,
        direction: "top",
      });
    }
  }

  mainMarker.value = L.marker([props.center.lat, props.center.lng], {
    icon: buildMainIcon(L),
    zIndexOffset: 1000,
    title: props.title || "Dreamland Oasis",
  })
    .addTo(map)
    .bindPopup(
      `<strong>${props.title || "Dreamland Oasis"}</strong><br/><span style="color:#b4942e;font-size:0.8rem;letter-spacing:0.04em;">${t("map.reception")}</span>`
    );
  mainMarker.value.on("click", () => {
    if (territoryPolygon.value) {
      map.flyToBounds(territoryPolygon.value.getBounds(), {
        padding: [48, 48],
        duration: 0.6,
        maxZoom: 17,
      });
    }
  });

  pointMarkers.value = props.points.map((p, i) => {
    const marker = L.circleMarker([p.lat, p.lng], {
      radius: 7,
      color: "#0f2c3e",
      weight: 1.5,
      fillColor: "#ffffff",
      fillOpacity: 1,
      className: "dl-map-point",
    })
      .addTo(map)
      .bindTooltip(p.label, { direction: "top", offset: [0, -4] });
    marker.on("mouseover", () => emit("point:hover", i));
    marker.on("mouseout", () => emit("point:hover", null));
    return marker;
  });

  mapInstance.value = map;

  if (territoryPolygon.value) {
    map.fitBounds(territoryPolygon.value.getBounds(), {
      padding: [48, 48],
      maxZoom: 17,
    });
  }

  requestAnimationFrame(() => {
    map.invalidateSize();
    if (territoryPolygon.value) {
      map.fitBounds(territoryPolygon.value.getBounds(), {
        padding: [48, 48],
        maxZoom: 17,
      });
    }
  });
  setTimeout(() => map.invalidateSize(), 250);

  loadRoutes(L, map);
}

async function loadRoutes(L: typeof import("leaflet"), map: LeafletMap) {
  try {
    const res = await fetch("/data/location-routes.json");
    if (!res.ok) return;
    const data = (await res.json()) as { routes: StoredRoute[] };
    const layers = data.routes.map((r) => {
      const latlngs = r.coordinates.map(
        ([lng, lat]) => [lat, lng] as LatLngTuple
      );
      return L.polyline(latlngs, {
        color: BRAND_GOLD,
        weight: 2,
        opacity: 0.4,
        dashArray: "4 6",
        lineCap: "round",
        className: "dl-map-route",
      }).addTo(map);
    });
    // Route lines sit under markers so pins remain clickable.
    layers.forEach((l) => l.bringToBack());
    routeLayers.value = layers;
    // If the user already focused a row while routes were still loading,
    // re-apply the highlight so the corresponding polyline lights up.
    if (props.activeIndex != null) highlightActive(props.activeIndex);
  } catch (err) {
    console.warn("[LocationMap] could not load routes", err);
  }
}

function highlightActive(i: number | null) {
  const map = mapInstance.value;
  if (!map) return;
  pointMarkers.value.forEach((m, idx) => {
    if (idx === i) {
      m.setStyle({
        radius: 11,
        color: BRAND_GOLD_DARK,
        weight: 3,
        fillColor: BRAND_GOLD,
      });
      m.bringToFront();
      m.openTooltip();
    } else {
      m.setStyle({
        radius: 7,
        color: "#0f2c3e",
        weight: 1.5,
        fillColor: "#ffffff",
      });
    }
  });
  routeLayers.value.forEach((layer, idx) => {
    if (idx === i) {
      layer.setStyle({
        color: BRAND_GOLD_DARK,
        weight: 5,
        opacity: 0.95,
        dashArray: undefined,
      });
      layer.bringToFront();
    } else {
      layer.setStyle({
        color: BRAND_GOLD,
        weight: 2,
        opacity: 0.35,
        dashArray: "4 6",
      });
      layer.bringToBack();
    }
  });
  if (i != null && routeLayers.value[i]) {
    map.flyToBounds(routeLayers.value[i].getBounds(), {
      padding: [48, 48],
      duration: 0.6,
      maxZoom: 15,
    });
  } else if (i != null && props.points[i]) {
    const p = props.points[i];
    map.flyTo([p.lat, p.lng], Math.max(map.getZoom(), 12), {
      animate: true,
      duration: 0.6,
    });
  }
}

watch(
  () => props.activeIndex,
  (i) => highlightActive(i ?? null)
);

onMounted(() => {
  initMap();
});

onBeforeUnmount(() => {
  mapInstance.value?.remove();
  mapInstance.value = undefined;
});
</script>

<template>
  <div class="relative w-full" :style="{ height }">
    <div
      ref="mapEl"
      class="h-full w-full rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800"
      role="region"
      :aria-label="title || 'Dreamland Oasis map'"
    />
    <noscript>
      <div class="absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-800">
        <img
          v-if="poster"
          :src="poster"
          alt="Dreamland Oasis, Chakvi"
          class="h-full w-full object-cover"
        >
      </div>
    </noscript>
  </div>
</template>

<style>
.dl-map-main-pin {
  background: transparent;
  border: none;
}
.dl-pin svg {
  display: block;
  filter: drop-shadow(0 3px 6px rgba(15, 44, 62, 0.25));
}

.leaflet-container {
  font-family: var(--font-body);
  background: #eef2f4;
  outline: none;
}

.leaflet-tile-container {
  filter: saturate(0.85) contrast(0.96);
}

.leaflet-bar,
.leaflet-bar a {
  border: none !important;
  box-shadow: 0 4px 12px rgba(15, 44, 62, 0.12);
}
.leaflet-bar a {
  background: rgba(255, 255, 255, 0.96);
  color: #0f2c3e;
  font-weight: 300;
  transition: background 0.2s ease, color 0.2s ease;
}
.leaflet-bar a:first-child {
  border-top-left-radius: 9999px;
  border-top-right-radius: 9999px;
}
.leaflet-bar a:last-child {
  border-bottom-left-radius: 9999px;
  border-bottom-right-radius: 9999px;
  border-top: 1px solid rgba(15, 44, 62, 0.08) !important;
}
.leaflet-bar a:hover {
  background: #d4af37;
  color: #fff;
}
.dark .leaflet-bar a {
  background: rgba(22, 60, 82, 0.96);
  color: #f8fafc;
}
.dark .leaflet-bar a:hover {
  background: #d4af37;
  color: #0f2c3e;
}

.leaflet-popup-content-wrapper {
  border-radius: 14px;
  padding: 2px 4px;
  background: #ffffff;
  color: #0f2c3e;
  box-shadow: 0 10px 30px -12px rgba(15, 44, 62, 0.35);
  border: 1px solid rgba(212, 175, 55, 0.35);
}
.leaflet-popup-content {
  margin: 0.7rem 1rem;
  font-size: 0.9rem;
  line-height: 1.4;
}
.leaflet-popup-content strong {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 1.05rem;
  color: #0f2c3e;
  letter-spacing: 0.01em;
}
.leaflet-popup-tip {
  background: #ffffff;
  border: 1px solid rgba(212, 175, 55, 0.35);
  box-shadow: 0 6px 14px -4px rgba(15, 44, 62, 0.25);
}
.dark .leaflet-popup-content-wrapper,
.dark .leaflet-popup-tip {
  background: #163c52;
  color: #fff;
  border-color: rgba(212, 175, 55, 0.5);
}
.dark .leaflet-popup-content strong {
  color: #f8fafc;
}

.leaflet-tooltip {
  background: rgba(15, 44, 62, 0.92);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 4px 9px;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 8px rgba(15, 44, 62, 0.25);
}
.leaflet-tooltip-top:before { border-top-color: rgba(15, 44, 62, 0.92); }
.leaflet-tooltip-bottom:before { border-bottom-color: rgba(15, 44, 62, 0.92); }
.leaflet-tooltip-left:before { border-left-color: rgba(15, 44, 62, 0.92); }
.leaflet-tooltip-right:before { border-right-color: rgba(15, 44, 62, 0.92); }

.leaflet-control-attribution {
  background: rgba(255, 255, 255, 0.82) !important;
  color: #475569;
  font-size: 10px;
  padding: 2px 8px;
  border-top-left-radius: 6px;
  backdrop-filter: blur(4px);
}
.leaflet-control-attribution a {
  color: #64748b;
}

.dl-map-point {
  transition: r 0.2s ease, fill 0.2s ease, stroke 0.2s ease;
}
</style>
